import { useState, useEffect, useRef } from 'react';
import { getSpineManager } from '../../lib/spine-manager';
import { getPixiApp } from '../../lib/pixi-app';
import type { Layer } from '@spine-workspace/shared';

const RULER_SIZE = 20; // must match SceneCanvas

interface BoneInfo {
  name: string;
  parentName: string | null;
  isChain: boolean;
  isIkTarget: boolean;
}

/**
 * Renders a bone overlay for the selected Spine layer.
 *
 * Optimisation: React renders the SVG element tree ONCE when the bone structure
 * changes (skeleton loaded / layer switched). Per-frame position updates are
 * written imperatively via `element.setAttribute` inside a PixiJS ticker
 * callback — no React re-renders and no vpState prop dependency.
 */
export default function BoneOverlay({ layer }: { layer: Layer }) {
  const lineRefs = useRef<Map<string, SVGLineElement>>(new Map());
  const circleRefs = useRef<Map<string, SVGCircleElement>>(new Map());
  const [boneInfos, setBoneInfos] = useState<BoneInfo[]>([]);

  // Fetch bone structure + IK metadata once per layer (not per frame).
  useEffect(() => {
    const constraints = getSpineManager().getIkConstraints(layer.id);
    const chainBones  = new Set(constraints?.flatMap((c) => c.bones) ?? []);
    const targetBones = new Set(constraints?.map((c) => c.targetBone) ?? []);

    const positions = getSpineManager().getBonePositions(layer.id);
    if (positions) {
      setBoneInfos(
        positions.map((b) => ({
          name: b.name,
          parentName: b.parentName,
          isChain: chainBones.has(b.name),
          isIkTarget: targetBones.has(b.name),
        }))
      );
    } else {
      setBoneInfos([]);
    }

    // Clear stale element refs when layer changes
    lineRefs.current.clear();
    circleRefs.current.clear();
  }, [layer.id]);

  // Imperatively update bone positions on every PixiJS ticker frame.
  // No React state update → no re-render.
  useEffect(() => {
    const app = getPixiApp();
    if (!app) return;

    const fn = () => {
      const positions = getSpineManager().getBonePositions(layer.id);
      if (!positions || positions.length === 0) return;

      const boneMap = new Map(positions.map((b) => [b.name, b]));

      for (const pos of positions) {
        // Line from this bone to its parent
        const lineEl = lineRefs.current.get(pos.name);
        if (lineEl && pos.parentName) {
          const parent = boneMap.get(pos.parentName);
          if (parent) {
            lineEl.setAttribute('x1', String(parent.stageX));
            lineEl.setAttribute('y1', String(parent.stageY));
            lineEl.setAttribute('x2', String(pos.stageX));
            lineEl.setAttribute('y2', String(pos.stageY));
          }
        }
        // Dot at bone origin
        const circleEl = circleRefs.current.get(pos.name);
        if (circleEl) {
          circleEl.setAttribute('cx', String(pos.stageX));
          circleEl.setAttribute('cy', String(pos.stageY));
        }
      }
    };

    app.ticker.add(fn);
    return () => { app.ticker?.remove(fn); };
  }, [layer.id]);

  if (boneInfos.length === 0) return null;

  return (
    <svg
      data-testid="bone-overlay"
      className="absolute pointer-events-none"
      style={{
        left: RULER_SIZE,
        top: RULER_SIZE,
        width: `calc(100% - ${RULER_SIZE}px)`,
        height: `calc(100% - ${RULER_SIZE}px)`,
        overflow: 'visible',
        zIndex: 30,
      }}
    >
      {/* Bone lines — created once; positions updated imperatively by ticker */}
      {boneInfos.map((bone) => {
        if (!bone.parentName) return null;
        return (
          <line
            key={`line-${bone.name}`}
            ref={(el) => {
              if (el) lineRefs.current.set(bone.name, el);
              else lineRefs.current.delete(bone.name);
            }}
            x1={0} y1={0} x2={0} y2={0}
            stroke={bone.isChain ? 'rgba(129,236,255,0.7)' : 'rgba(252,211,77,0.7)'}
            strokeWidth={bone.isChain ? 2 : 1.5}
            strokeLinecap="round"
            strokeDasharray={bone.isChain ? '4 2' : undefined}
          />
        );
      })}

      {/* Bone dots — IK target bones rendered by IKHandleOverlay */}
      {boneInfos.map((bone) => {
        if (bone.isIkTarget) return null;
        return (
          <circle
            key={bone.name}
            data-testid={`bone-dot-${bone.name}`}
            ref={(el) => {
              if (el) circleRefs.current.set(bone.name, el);
              else circleRefs.current.delete(bone.name);
            }}
            cx={0} cy={0}
            r={bone.isChain ? 5 : 4}
            fill={bone.isChain ? 'rgba(129,236,255,0.9)' : 'rgba(252,211,77,0.9)'}
            stroke="rgba(0,0,0,0.45)"
            strokeWidth={1}
          />
        );
      })}
    </svg>
  );
}
